<script setup lang="ts">
import LogoutBtn from '@/Components/LogoutBtn.vue';
import MessageBox from '@/Components/MessageBox.vue';
import RulesetTable from '@/Components/RulesetTable.vue';
import ScrollView from '@/Components/ScrollView.vue';
import Instructions from '@/Components/Instructions.vue';
import { useConsultantStore } from '@/Stores/ConsultantStore';
import { computed, inject, onMounted, reactive, Ref, ref, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import SessionView from '@/Components/SessionView.vue';
import { ErrRef } from '@/objects';
import { useSessionStore } from '@/Stores/AppStore';
import SupportButton from '@/Components/SupportButton.vue';

const page = usePage()
const config = page.props.experimentConfigData

const sessionStore = useSessionStore()
const cStore = useConsultantStore();
cStore.initialize();

const error = inject<ErrRef>("error")
const showLogout = inject<Ref<Boolean>>('show-logout')

//FIXME: Store this in the browser's localStorage so that it persists reloading without storing this in the DB
const unreads = ref<number[]>([])

// The consultant's own instructions, reachable at any time from the sidebar.
const instructionsShown = ref(false)

// The participant's own instructions (Part 1 / Part 2), so the consultant can read exactly
// what their participant was given. The consultant app is human-only, so the participant has
// a human consultant (hasChat, human assistant-intro variant).
const participantInstructionsShown = ref(false)
const participantInstructionsPart = ref(1)

function showParticipantInstructions(part: number) {
    participantInstructionsPart.value = part
    participantInstructionsShown.value = true
}

//Just a bunch of useful getters to better interface with the underlying store data
class ChatData {
    public id = -1

    constructor(id: number) {
        this.id = id
    }

    get name() {
        return `Partecipante ${this.id}`
    }

    get participant() {
        return cStore.state.participants[this.id]
    }

    get session() {
        return this.participant.session
    }

    // currentPhase/currentRound are null once the session is completed; fall back to the
    // last phase/round so a finished conversation can still render its header.
    get phase() {
        return this.session.currentPhase ?? this.session.phase2
    }

    get ruleset() {
        return this.phase.ruleset
    }

    get lastMsgId() {
        return this.participant.pendingMsgId
    }

    // If the last round is completed then there's no current round to get the idx from
    get roundIdx() {
        return (this.session.currentRound ?? this.phase.roundAt(-1))?.idx ?? 0
    }

    get open() {
        return cStore.currentParticipant === this.participant
    }

    get isVoting() {
        return this.session.phase1.completed && !this.session.voteDone
    }

    // The consultant's job for this participant is done once both phases are over:
    // currentPhase goes null when phase2 is completed (its last round's action chosen).
    get completed() {
        return this.session.currentPhase === null
    }

    get disconnected() {
        return this.participant.disconnected
    }

    get active() {
        return !this.completed && !this.disconnected
    }

    get canReply() {
        return this.active && !this.canReceive
    }

    get canReceive() {
        return this.active && this.lastMsgId < 0
    }
}

const chats = reactive<{[id: number]: ChatData}>({})
const currentChat = computed(() => {
    if (!cStore.currentParticipant) {
        return null
    }
    
    return chats[cStore.currentParticipant.id]
})

const replyTxt = ref("")
const finalTitle = computed(() => {
    if (!currentChat.value || currentChat.value.active) {
        return null
    }

    if (currentChat.value.completed) {
        return "Consulenza completata"
    }
    else if (currentChat.value.disconnected) {
        return "Partecipante eliminato"
    }
})
const finalSubtitle = computed(() => {
    if (!currentChat.value || currentChat.value.active) {
        return null
    }

    if (currentChat.value.completed) {
        return "Questo partecipante ha concluso tutte le fasi del gioco. La conversazione è terminata."
    }
    else if (currentChat.value.disconnected) {
        return "Questo partecipante è stato eliminato per inattività."
    }
})

function markAsRead(partId) {
    // I hate that JS doesnt have a clean "array.remove" method
    unreads.value = unreads.value.filter(v => v !== partId)
}

function reply() {
    if (!replyTxt.value) {
        return
    }

    cStore.sendReply(replyTxt.value, currentChat.value.participant.id)
    
    replyTxt.value = ""
}

watch(currentChat, (newC) => {
    markAsRead(newC.id)
})

onMounted(() => {
    showLogout.value = false
    console.log(`Playing as consultant: ${cStore.state.id}`)

    console.log("Store content:")
    console.log(cStore)
    
    for (const partId in cStore.state.participants) {
        chats[partId] = new ChatData(parseInt(partId))
    }

    cStore.onPartMessage = (partId, msgTxt, msgId) => {
        if (!chats[partId].open) {
            unreads.value.push(partId)
        }
    }

    cStore.onDisconnected = (partId) => {
        markAsRead(partId)
    }

    cStore.onError = (err) => {
        error.value = err
    }
})
</script>

<template>
    <template v-if="!sessionStore.paused">
        <Instructions v-model="instructionsShown" slug="consultant_human_instructions" title="Istruzioni per l'assistente" />
        <Instructions v-model="participantInstructionsShown" :part="participantInstructionsPart" :chat-type="'human'" />
    </template>
    <div class="flex w-full h-full">
        <!-- Same shell as the participant's sidebar (see Play.vue's `.side-container`): the
             warm grey background and the 12px padding, so both apps read as one product. -->
        <div class="w-1/3 col p-3 gap-3 bg-pink-650">
            <div class="side-row row items-center justify-between gap-2">
                <p class="font-semibold text-xl">
                    Conversazioni
                </p>
            </div>
            <ScrollView class="flex-1" :inner-classes="'col gap-2'">
                <button v-for="chat in chats" @click="cStore.selectParticipant(chat.id)"
                :data-testid="`chat-${chat.id}`" :data-state="chat.completed ? 'completed' : (chat.disconnected ? 'disconnected' : (chat.canReply ? 'pending' : 'idle'))"
                :class="[{'opacity-50': chat.completed}, chat === currentChat ? 'bg-white' : 'bg-pink']"
                class="btn-secondary side-row text-left row items-center border-pink-800 border-3">
                    <div class="flex-1">
                        <span>{{ chat.name }}</span><br>
                        <span class="text-xs opacity-75">
                            <span v-if="chat.completed">
                                Completato
                            </span>
                            <span v-else-if="chat.disconnected">
                                Disconnesso
                            </span>
                            <span v-else-if="chat.isVoting">
                                Votazione
                            </span>
                            <span v-else>
                                Fase {{ chat.session.currentPhase.idx }} - Round {{ chat.roundIdx+1 }}
                            </span>
                        </span>
                    </div>
                    <span v-if="chat.completed" class="mr-4 px-2.5 py-0.5 rounded-full bg-blue text-white text-xs font-semibold tracking-wide">&#x2714</span>
                    <span v-else-if="unreads.includes(chat.id) && !chat.open" class="size-4 mr-8 block bg-warning rounded-full"></span>
                </button>
            </ScrollView>
            <!-- Both actions span the sidebar's full width and keep the participant's
                 primary/secondary pairing, rather than sitting side by side. -->
            <div class="col gap-2 shrink-0">
                <button @click="instructionsShown = true" class="btn-primary px-4! py-2!">
                    Istruzioni
                </button>
                <SupportButton variant="secondary" class="justify-center"></SupportButton>
            </div>
            <div class="side-row row items-center gap-3 border-t-2 border-pink-800 pt-3">
                <div class="col flex-1 min-w-0">
                    <span class="text-xs text-gray-dark leading-none">Consulente</span>
                    <span class="font-semibold truncate" :title="cStore.state.email">{{ cStore.state.email }}</span>
                </div>
                <LogoutBtn />
            </div>
        </div>
        <div v-if="currentChat" class="flex-1 col p-5 gap-4 relative">
            <SessionView :session="currentChat.session" :flip-chat="true">
                <template #before>
                    <!-- idk why but I have to add the right-side padding back in -->
                    <div class="bg-gray-400 rounded-md row gap-3 p-3 items-center">
                        <RulesetTable :ruleset="currentChat.session.phase1.ruleset" class="h-full min-w-[40%]" />
                        <div class="flex-1 col items-start gap-2 text-sm">
                            <p>Per leggere le istruzioni della fase 1 fornite al partecipante, clicca sul pulsante "Istruzioni".</p>
                            <button @click="showParticipantInstructions(1)" class="btn-primary px-4! py-2!">
                                Istruzioni
                            </button>
                        </div>
                    </div>
                </template>
                <template #choices>
                    <div class="text-sm text-white rounded-sm px-2 py-1 row items-center bg-pink-900">
                        Il partecipante ha scelto
                    </div>
                </template>
                <template #vote>
                    <!-- FIXME: duplicated from #before slot, needs cleanup -->
                    <!-- idk why but I have to add the right-side padding back in -->
                    <div v-if="currentChat.session.phase2.ruleset" class="bg-gray-400 rounded-md row gap-3 p-3 items-center">
                        <RulesetTable :ruleset="currentChat.session.phase2.ruleset" class="h-full min-w-[40%]" />
                        <div class="flex-1 col items-start gap-2 text-sm">
                            <p>Per leggere le istruzioni della fase 2 fornite al partecipante, clicca sul pulsante "Istruzioni".</p>
                            <button @click="showParticipantInstructions(2)" class="btn-primary px-4! py-2!">
                                Istruzioni
                            </button>
                        </div>
                    </div>
                </template>
                <template #after>
                    <div v-if="currentChat.isVoting" class="text-sm text-center text-white rounded-sm px-2 py-1 bg-pink-900">
                        Il partecipante sta votando il ruleset della prossima fase
                    </div>
                </template>
            </SessionView>
            <MessageBox v-if="currentChat.active" v-model="replyTxt" @send="reply" :can-send="currentChat.canReply" :max-len="config['maxReplyLen'] ?? -1"/>
            <div v-else class="min-h-12 rounded-sm border-4 border-blue-200 bg-blue-100 p-3 col items-center justify-center gap-1 text-center">
                <span class="font-semibold text-blue">{{ finalTitle }}</span>
                <span class="text-gray-dark">
                    {{ finalSubtitle }}
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.side-row {
    @apply p-2 min-h-14;
}

.rnd {
    @apply rounded-md;
}

.btn-small {
    @reference 'tailwindcss';
    @apply rounded-md text-white size-14;
}
</style>